import Checkbox from "PFComponents/checkbox/checkbox";
import AutoSelect from "PFComponents/select/autoselect";
import Select from "PFComponents/select/select";
import { SimpleData } from "PFCore/hooks/queries/insights/use_skills_bar_query";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { useIsSkillFilterActive } from "../../../hooks/use_is_skill_filter_active";
import { SelectorOption, ShowBy, TopNOption } from "../../../hooks/use_supply_demand_section";
import { useSupplyTotalDemandSelectors } from "../../../hooks/use_supply_total_demand_selectors";
import css from "./supply_total_demand_section.module.scss";

type SupplyTotalDemandSelectorsProps = {
  loading: boolean;
  showBy: string;
  setShowBy: Dispatch<SetStateAction<ShowBy>>;
  bars: SelectorOption[];
  skills: {
    label: string;
    value: string;
  }[];
  selectedSkills: SelectorOption[];
  setSelectedSkills: Dispatch<SetStateAction<SelectorOption[]>>;
  setBars: Dispatch<SetStateAction<SelectorOption[]>>;
  sortBy: string | null;
  setSortBy: Dispatch<SetStateAction<string | null>>;
  topN: TopNOption;
  setTopN: Dispatch<SetStateAction<TopNOption>>;
  skillCategories: any[];
  setSkillCategories: Dispatch<SetStateAction<any[]>>;
  dropdown: SimpleData[];
};

const SupplyTotalDemandSelectors = ({
  loading,
  showBy,
  setShowBy,
  setSkillCategories,
  bars,
  skills,
  selectedSkills,
  setSelectedSkills,
  setBars,
  sortBy,
  setSortBy,
  topN,
  setTopN,
  skillCategories,
  dropdown
}: SupplyTotalDemandSelectorsProps): JSX.Element => {
  const isSkillFilterActive = useIsSkillFilterActive();

  const { t } = useTranslation("insights");
  const { barsOptions, sortOptions, sortByOptions, topNumberOptions } = useSupplyTotalDemandSelectors();

  return (
    <div className={css.supplyTotalDemandSectionFilters}>
      <div className={css.supplyTotalDemandSectionInputs}>
        <div className={css.supplyTotalDemandSectionCheckboxes}>
          <strong>{t("filters.showBy")}</strong>
          <Checkbox
            disabled={loading}
            handleChange={() => {
              setShowBy("category_1");
              setSkillCategories([]);
            }}
            checked={showBy === "category_1"}
            label={t("filters.category")}
          />
          <Checkbox
            disabled={loading}
            handleChange={() => {
              setShowBy("skill");
              setSkillCategories([]);
            }}
            checked={showBy === "skill"}
            label={t("filters.skills")}
          />
        </div>
        <div className={css.supplyTotalDemandSectionBars}>
          <AutoSelect
            style={{ marginRight: 20 }}
            key="bars"
            placeholder={t("filters.bars")}
            multi
            closeOnChange
            values={bars}
            letClear
            query={() =>
              Promise.resolve(
                Object.keys(barsOptions).map((item) => ({
                  text: barsOptions[item],
                  value: item
                }))
              )
            }
            handleChange={(values) => setBars(values)}
            disabled={loading}
            rootClassName={css.supplyTotalDemandSectionCategoriesSelect}
          />
        </div>
        <div className={css.supplyTotalDemandSectionCheckboxes}>
          <Select
            /* @ts-ignore */
            letClear
            label={t("filters.sortBy")}
            controlledValue
            value={sortBy ? sortOptions[sortBy] : null}
            options={sortByOptions}
            handleChange={setSortBy}
          />
        </div>
        <div className={css.supplyTotalDemandSectionCheckboxes}>
          <Select
            /* @ts-ignore */
            letClear
            label={t("filters.coreN")}
            controlledValue
            value={topN}
            options={topNumberOptions}
            handleChange={setTopN}
          />
        </div>
      </div>
      <div className={css.supplyTotalDemandSectionCategories}>
        <AutoSelect
          label={t("filters.skillsCategories")}
          key={showBy}
          placeholder={t("selectValues")}
          multi
          closeOnChange
          values={skillCategories}
          letClear
          query={(term) =>
            Promise.resolve(
              dropdown
                .map((item) => ({ text: item.label, value: item.value }))
                .filter((item) => item.text.toLowerCase().includes(term.toLowerCase()))
            )
          }
          handleChange={(values) => setSkillCategories(values)}
          disabled={loading}
          rootClassName={css.supplyTotalDemandSectionCategoriesSelect}
        />
        {showBy === "skill" && !isSkillFilterActive && (
          <AutoSelect
            label="Skills"
            key={`${showBy}+`}
            placeholder={t("selectValues")}
            multi
            closeOnChange
            values={selectedSkills}
            letClear
            query={(term) =>
              Promise.resolve(
                skills
                  .map((item) => ({ text: item.label, value: item.value }))
                  .filter((item) => item.text.toLowerCase().includes(term.toLowerCase()))
              )
            }
            handleChange={(values) => setSelectedSkills(values)}
            disabled={loading}
            rootClassName={css.supplyTotalDemandSectionCategoriesSelect}
          />
        )}
      </div>
    </div>
  );
};

export default SupplyTotalDemandSelectors;
